/**
 * Logistics P&L — Cost view.
 * A facility's logistics cost: daily (fixed + adhoc + total) and weekly (week1..4) buckets,
 * plus an optional "Accrue" button that writes the month into the EBITDA Logistics line.
 * City-scoped — facility auto-selects for a City Head.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useFacilities } from '../../manpower-workers/hooks/useFacilities';
import { useDailyCost, useWeeklyCost, useAccrue } from '../hooks/useLogistics';

const todayIso = () => {
	const n = new Date();
	return new Date(n.getTime() - n.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const monthStart = () => `${todayIso().slice(0, 7)}-01`;
const money = (v?: string | number | null) => `₹${Number(v ?? 0).toLocaleString('en-IN')}`;

export const LogisticsCost: React.FC = () => {
	const user = useSelector((state: RootState) => state.auth.user);
	const { data: facilities = [] } = useFacilities(user?.city_id ?? undefined);

	const [facilityId, setFacilityId] = useState<number | ''>('');
	const [date, setDate] = useState(todayIso());
	const [month, setMonth] = useState(monthStart());
	const [toast, setToast] = useState<string | null>(null);

	const fid = facilityId === '' ? undefined : facilityId;
	const { data: daily = [], isLoading: dailyLoading } = useDailyCost(fid, date);
	const { data: weekly, isLoading: weeklyLoading } = useWeeklyCost(fid, month);
	const accrue = useAccrue();

	useEffect(() => {
		if (facilityId === '' && facilities.length > 0) setFacilityId(facilities[0].id);
	}, [facilityId, facilities]);

	const showToast = (m: string) => {
		setToast(m);
		window.setTimeout(() => setToast(null), 4000);
	};

	const handleAccrue = async () => {
		if (fid == null) return;
		try {
			const r = await accrue.mutateAsync({ facilityId: fid, month });
			const total = r.seam?.total ?? (r.week1 + r.week2 + r.week3 + r.week4);
			showToast(`Accrued — ₹${Number(total).toLocaleString('en-IN')} written to EBITDA`);
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Accrue failed');
		}
	};

	const inputCls = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';
	const weeklyTotal = weekly ? weekly.week1 + weekly.week2 + weekly.week3 + weekly.week4 : 0;

	return (
		<div className='min-h-screen bg-white p-6'>
			<div className='max-w-5xl mx-auto'>
				<div className='mb-6 flex items-start justify-between'>
					<div>
						<h1 className='text-xl font-semibold text-gray-900'>Logistics Cost</h1>
						<p className='text-sm text-gray-500'>Logistics P&amp;L — fixed (contracted vehicles) + ad-hoc cost by day and week.</p>
					</div>
					<button type='button' onClick={handleAccrue} disabled={fid == null || accrue.isPending} className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
						{accrue.isPending ? 'Accruing…' : 'Refresh / Accrue'}
					</button>
				</div>

				<div className='mb-4 flex flex-wrap items-end gap-3'>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Facility</label>
						<select value={facilityId} onChange={e => setFacilityId(e.target.value === '' ? '' : Number(e.target.value))} className={`${inputCls} w-64`}>
							<option value=''>Select a facility…</option>
							{facilities.map(f => <option key={f.id} value={f.id}>{f.location}</option>)}
						</select>
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Daily date</label>
						<input type='date' value={date} onChange={e => setDate(e.target.value)} className={inputCls} />
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Month</label>
						<input type='month' value={month.slice(0, 7)} onChange={e => setMonth(e.target.value ? `${e.target.value}-01` : '')} className={inputCls} />
					</div>
				</div>

				{/* Daily */}
				<div className='mb-6 rounded-lg border border-gray-200 p-4'>
					<h2 className='mb-3 text-sm font-semibold text-gray-900'>Daily cost</h2>
					{fid == null ? <p className='text-sm text-gray-500'>Pick a facility.</p>
						: dailyLoading ? <p className='text-sm text-gray-500'>Loading…</p>
						: daily.length === 0 ? <p className='text-sm text-gray-500'>No cost recorded for this date.</p>
						: (
							<div className='flex flex-wrap gap-6'>
								{daily.map((d, i) => (
									<React.Fragment key={i}>
										<Stat label='Fixed' value={money(d.fixed_cost)} />
										<Stat label='Ad-hoc' value={money(d.adhoc_cost)} />
										<Stat label='Total' value={money(d.total_cost)} accent />
									</React.Fragment>
								))}
							</div>
						)}
				</div>

				{/* Weekly */}
				<div className='rounded-lg border border-gray-200 p-4'>
					<h2 className='mb-3 text-sm font-semibold text-gray-900'>Monthly (week buckets)</h2>
					{fid == null ? <p className='text-sm text-gray-500'>Pick a facility.</p>
						: weeklyLoading ? <p className='text-sm text-gray-500'>Loading…</p>
						: !weekly ? <p className='text-sm text-gray-500'>No monthly cost recorded.</p>
						: (
							<div className='flex flex-wrap gap-6'>
								<Stat label='Week 1' value={money(weekly.week1)} />
								<Stat label='Week 2' value={money(weekly.week2)} />
								<Stat label='Week 3' value={money(weekly.week3)} />
								<Stat label='Week 4' value={money(weekly.week4)} />
								<Stat label='Total' value={money(weeklyTotal)} accent />
							</div>
						)}
				</div>
			</div>

			{toast && <div className='fixed bottom-6 right-6 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg'>{toast}</div>}
		</div>
	);
};

const Stat: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
	<div>
		<div className='text-[11px] uppercase tracking-wide text-gray-500'>{label}</div>
		<div className={`text-lg font-semibold ${accent ? 'text-green-700' : 'text-gray-900'}`}>{value}</div>
	</div>
);

export default LogisticsCost;
